import { useEffect, useRef, useState } from "react";
import "../../styles/Home-page-component/WhyChooseUs.css";

/* ─── Animated counter ─── */
function Counter({ to, duration = 1600, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const el = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !fired.current) {
          fired.current = true;
          const t0 = performance.now();
          const run = (now) => {
            const p = Math.min((now - t0) / duration, 1);
            const ease = 1 - (1 - p) ** 3;
            setVal(Math.round(ease * to));
            if (p < 1) requestAnimationFrame(run);
          };
          requestAnimationFrame(run);
        }
      },
      { threshold: 0.4 }
    );
    if (el.current) io.observe(el.current);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={el}>{prefix}{val.toLocaleString()}{suffix}</span>;
}

/* ─── Ring progress ─── */
function RingProgress({ pct }) {
  const R = 42, C = 2 * Math.PI * R;
  const [dash, setDash] = useState(C);
  const el = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && !fired.current) {
          fired.current = true;
          const t0 = performance.now(), dur = 1800;
          const run = (now) => {
            const p = Math.min((now - t0) / dur, 1);
            const ease = 1 - (1 - p) ** 3;
            setDash(C * (1 - (ease * pct) / 100));
            if (p < 1) requestAnimationFrame(run);
          };
          requestAnimationFrame(run);
        }
      },
      { threshold: 0.4 }
    );
    if (el.current) io.observe(el.current);
    return () => io.disconnect();
  }, [pct, C]);

  return (
    <div className="ring-wrap" ref={el}>
      <svg viewBox="0 0 100 100" className="ring-svg">
        <circle cx="50" cy="50" r={R} className="ring-track" />
        <circle
          cx="50" cy="50" r={R}
          className="ring-fill"
          strokeDasharray={C}
          strokeDashoffset={dash}
          transform="rotate(-90 50 50)"
        />
      </svg>
      <div className="ring-text">
        <Counter to={pct} suffix="%" duration={1800} />
      </div>
    </div>
  );
}

/* ─── Sparkline ─── */
function Spark({ data, color }) {
  const W = 200, H = 52;
  const mn = Math.min(...data), mx = Math.max(...data);
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * W;
    const y = H - 6 - ((v - mn) / (mx - mn || 1)) * (H - 14);
    return [x, y];
  });
  const line = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const area =
    `M${pts[0][0]},${pts[0][1]} ` +
    pts.map(([x, y]) => `L${x},${y}`).join(" ") +
    ` L${W},${H} L0,${H} Z`;
  const id = `sg${color.replace(/[^a-z0-9]/gi, "")}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="spark" preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${id})`} />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3" fill={color} />
    </svg>
  );
}

const revData = [18, 24, 21, 30, 26, 35, 32, 40, 37, 44, 42, 50];
const custData = [110, 128, 120, 142, 155, 148, 168, 163, 180, 172, 190, 208];

/* ════════════════════════════ MAIN ════════════════════════════ */
export default function WhyChooseUs() {
  return (
    <section className="wcu">
      <div className="wcu-grid-bg" aria-hidden />
      <div className="glow glow-l" aria-hidden />
      <div className="glow glow-r" aria-hidden />

      <div className="wcu-container">
        <header className="wcu-header">
          <span className="eyebrow">🚀 Trusted by growing teams</span>
          <h2 className="wcu-heading">
            Why Businesses Choose<br />
            <span className="heading-accent">Us</span> Everyday
          </h2>
          <p className="wcu-desc">
            We help teams move faster, scale smarter, and work securely with AI agents
            they can trust — built with enterprise-grade security and seamless integrations.
          </p>
        </header>

        <div className="bento">

          {/* ── Card 1: Real Business Impact ── */}
          <div className="card c-impact">
            <div className="c-body">
              <div className="row-sb">
                <div>
                  <p className="kpi-lbl">AVG. COST REDUCTION</p>
                  <p className="kpi-val">$<Counter to={33500} duration={1900} /></p>
                </div>
                <span className="pill green">↑ Live</span>
              </div>
              <RingProgress pct={92} />
              <p className="kpi-sub">saving $<Counter to={18500} duration={1800} /> in 30 days</p>
            </div>
            <p className="c-caption"><strong>Real Business Impact</strong> — Cut costs, save time without extra hires.</p>
          </div>

          {/* ── Card 2: Enterprise Security ── */}
          <div className="card c-security">
            <div className="c-body security-center">
              <div className="shield-wrap">
                <span className="s-ring r1" /><span className="s-ring r2" /><span className="s-ring r3" />
                <div className="shield-icon">🔒</div>
              </div>
              <div className="badge-row">
                {["SOC 2", "GDPR", "HIPAA"].map(b => (
                  <span key={b} className="sec-badge">{b}</span>
                ))}
              </div>
            </div>
            <p className="c-caption"><strong>Enterprise-Grade Security</strong> — Your data stays safe with full compliance.</p>
          </div>

          {/* ── Card 3: Actionable Insights ── */}
          <div className="card c-insights">
            <div className="c-body">
              <div className="row-sb">
                <div>
                  <p className="kpi-lbl">MONTHLY REVENUE</p>
                  <p className="kpi-val cyan">+$<Counter to={2500} duration={1600} /></p>
                </div>
                <span className="pill blue">↑ 18%</span>
              </div>
              <Spark data={revData} color="#10b981" />
              <div className="row-sb" style={{ marginTop: "0.6rem" }}>
                <p className="kpi-lbl">ACTIVE CUSTOMERS</p>
                <p className="kpi-val sm"><Counter to={18700} duration={1800} /></p>
              </div>
              <Spark data={custData} color="#2f80ed" />
            </div>
            <p className="c-caption"><strong>Actionable Insights</strong> — Complex data into clear reports, fast.</p>
          </div>

          {/* ── Card 4: Always-On AI Agents ── */}
          {/* ── Card 4: AI Processing Pipeline ── */}
          <div className="card c-workflow">
            <div className="c-body">

              <p className="kpi-lbl">AI PROCESSING PIPELINE</p>

              <div className="pipeline">

                <div className="pipe-step">
                  <div className="pipe-dot blue"></div>
                  <p className="pipe-title">Data Ingestion</p>
                  <p className="pipe-desc">
                    Collects infrastructure and usage data securely.
                  </p>
                </div>

                <div className="pipe-line"></div>

                <div className="pipe-step">
                  <div className="pipe-dot cyan"></div>
                  <p className="pipe-title">AI Analysis</p>
                  <p className="pipe-desc">
                    Scans workloads and detects inefficiencies.
                  </p>
                </div>

                <div className="pipe-line"></div>

                <div className="pipe-step">
                  <div className="pipe-dot green"></div>
                  <p className="pipe-title">Optimization</p>
                  <p className="pipe-desc">
                    Generates cost-saving recommendations.
                  </p>
                </div>

                <div className="pipe-line"></div>

                <div className="pipe-step">
                  <div className="pipe-dot purple"></div>
                  <p className="pipe-title">Reporting</p>
                  <p className="pipe-desc">
                    Delivers actionable performance insights.
                  </p>
                </div>

              </div>

            </div>

            <p className="c-caption">
              <strong>AI Processing Pipeline</strong> — Automated workflow transforming raw data into optimized infrastructure decisions.
            </p>
          </div>

          {/* ── Card 5: Developer Productivity ── */}
          <div className="card c-devprod">
            <div className="c-body">
              <div className="row-sb">
                <p className="kpi-lbl">DEPLOY FREQUENCY</p>
                <span className="pill purple">+41%</span>
              </div>
              <div className="bars">
                {[65, 78, 72, 95].map((h, i) => (
                  <div key={i} className="bar-col">
                    <div className="bar" style={{ "--h": `${h}%`, animationDelay: `${i * 100}ms` }} />
                    <span className="bar-lbl">{["Q1", "Q2", "Q3", "Q4"][i]}</span>
                  </div>
                ))}
              </div>
              <p className="kpi-sub">Avg. pipeline: <strong style={{ color: "var(--cyan)" }}>4.2 min</strong></p>
            </div>
            <p className="c-caption"><strong>Developer Productivity</strong> — AI-augmented tooling for faster shipping.</p>
          </div>

        </div>
      </div>
    </section>
  );
}